import { useState, useEffect, useRef } from 'react'
import { Table, Button, Badge, Modal, Form, Card, Alert, ProgressBar } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { firmwareApi } from '../services/api'
import { useConfirm } from '../components/common/ConfirmDialog'

interface FirmwareFile {
  id: string
  phone_model: string
  version: string
  original_filename: string
  file_size: number
  md5_checksum: string | null
  is_active: boolean
  description: string | null
  created_at: string
  download_url: string
}

interface PhoneModelInfo {
  id: string
  name: string
  active_firmware: FirmwareFile | null
}

// List of supported phone models
const PHONE_MODELS = [
  { id: 'T19P_E2', name: 'Yealink T19P E2' },
  { id: 'T21P_E2', name: 'Yealink T21P E2' },
  { id: 'T23G', name: 'Yealink T23G' },
  { id: 'T27G', name: 'Yealink T27G' },
  { id: 'T29G', name: 'Yealink T29G' },
  { id: 'T31G', name: 'Yealink T31G' },
  { id: 'T33G', name: 'Yealink T33G' },
  { id: 'T40G', name: 'Yealink T40G' },
  { id: 'T41S', name: 'Yealink T41S' },
  { id: 'T42S', name: 'Yealink T42S' },
  { id: 'T43U', name: 'Yealink T43U' },
  { id: 'T46S', name: 'Yealink T46S' },
  { id: 'T46U', name: 'Yealink T46U' },
  { id: 'T48S', name: 'Yealink T48S' },
  { id: 'T48U', name: 'Yealink T48U' },
  { id: 'T53', name: 'Yealink T53' },
  { id: 'T53W', name: 'Yealink T53W' },
  { id: 'T54W', name: 'Yealink T54W' },
  { id: 'T57W', name: 'Yealink T57W' },
  { id: 'T58A', name: 'Yealink T58A' },
]

export default function Firmware() {
  const [firmware, setFirmware] = useState<FirmwareFile[]>([])
  const [models, setModels] = useState<PhoneModelInfo[]>([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [selectedModel, setSelectedModel] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const confirm = useConfirm()

  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    phone_model: '',
    version: '',
    description: '',
    set_active: true
  })

  const loadData = async () => {
    try {
      setLoading(true)
      const [firmwareRes, modelsRes] = await Promise.all([
        firmwareApi.list(),
        firmwareApi.listModels()
      ])
      setFirmware(firmwareRes.data.items || [])
      setModels(modelsRes.data || [])
    } catch {
      toast.error('Fehler beim Laden der Firmware-Daten')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  }

  const formatDate = (dateStr: string): string => {
    return new Date(dateStr).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const handleUpload = async () => {
    const file = fileInputRef.current?.files?.[0]
    if (!file) {
      toast.error('Bitte wählen Sie eine Firmware-Datei aus')
      return
    }

    if (!uploadForm.phone_model) {
      toast.error('Bitte wählen Sie ein Telefonmodell aus')
      return
    }

    if (!uploadForm.version) {
      toast.error('Bitte geben Sie eine Versionsnummer ein')
      return
    }

    const formData = new FormData()
    formData.append('file', file)
    formData.append('phone_model', uploadForm.phone_model)
    formData.append('version', uploadForm.version)
    formData.append('description', uploadForm.description)
    formData.append('set_active', uploadForm.set_active.toString())

    try {
      setUploading(true)
      setUploadProgress(0)

      await firmwareApi.upload(formData)

      toast.success('Firmware erfolgreich hochgeladen')
      setShowUploadModal(false)
      setUploadForm({ phone_model: '', version: '', description: '', set_active: true })
      if (fileInputRef.current) fileInputRef.current.value = ''
      loadData()
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } }
      toast.error(error.response?.data?.detail || 'Fehler beim Hochladen')
    } finally {
      setUploading(false)
      setUploadProgress(0)
    }
  }

  const handleActivate = async (fw: FirmwareFile) => {
    try {
      await firmwareApi.activate(fw.id)
      toast.success(`Firmware ${fw.version} für ${fw.phone_model} aktiviert`)
      loadData()
    } catch {
      toast.error('Fehler beim Aktivieren der Firmware')
    }
  }

  const handleDelete = async (fw: FirmwareFile) => {
    const confirmed = await confirm({
      title: 'Firmware löschen',
      message: `Firmware "${fw.original_filename}" (${fw.version}) wirklich löschen?`,
      confirmText: 'Löschen',
      variant: 'danger'
    })

    if (!confirmed) return

    try {
      await firmwareApi.delete(fw.id)
      toast.success('Firmware gelöscht')
      loadData()
    } catch {
      toast.error('Fehler beim Löschen der Firmware')
    }
  }

  // Filter firmware by selected model
  const filteredFirmware = selectedModel
    ? firmware.filter(fw => fw.phone_model === selectedModel)
    : firmware

  // Get model name by ID
  const getModelName = (modelId: string): string => {
    const model = PHONE_MODELS.find(m => m.id === modelId)
    return model?.name || modelId
  }

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Laden...</span>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          <i className="bi bi-download me-2"></i>
          Firmware-Verwaltung
        </h2>
        <Button variant="primary" onClick={() => setShowUploadModal(true)}>
          <i className="bi bi-upload me-2"></i>
          Firmware hochladen
        </Button>
      </div>

      {/* Active Firmware Overview */}
      <Card className="mb-4">
        <Card.Header>
          <i className="bi bi-check-circle me-2"></i>
          Aktive Firmware pro Modell
        </Card.Header>
        <Card.Body>
          <div className="row">
            {models.map(model => (
              <div key={model.id} className="col-md-4 col-lg-3 mb-3">
                <div className={`border rounded p-3 h-100 ${model.active_firmware ? 'border-success' : 'border-secondary'}`}>
                  <div className="fw-bold">{model.name}</div>
                  {model.active_firmware ? (
                    <div className="mt-2">
                      <Badge bg="success">v{model.active_firmware.version}</Badge>
                      <div className="small text-muted mt-1">
                        {formatFileSize(model.active_firmware.file_size)}
                      </div>
                    </div>
                  ) : (
                    <div className="text-muted mt-2">
                      <i className="bi bi-x-circle me-1"></i>
                      Keine Firmware
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card.Body>
      </Card>

      {/* Firmware List */}
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <span>
            <i className="bi bi-file-earmark-binary me-2"></i>
            Alle Firmware-Dateien ({firmware.length})
          </span>
          <Form.Select
            size="sm"
            style={{ width: 'auto' }}
            value={selectedModel || ''}
            onChange={e => setSelectedModel(e.target.value || null)}
          >
            <option value="">Alle Modelle</option>
            {PHONE_MODELS.map(model => (
              <option key={model.id} value={model.id}>{model.name}</option>
            ))}
          </Form.Select>
        </Card.Header>
        <Card.Body>
          {filteredFirmware.length === 0 ? (
            <Alert variant="info" className="mb-0">
              <i className="bi bi-info-circle me-2"></i>
              Keine Firmware-Dateien vorhanden. Laden Sie eine Firmware hoch, um sie für das Provisioning zu verwenden.
            </Alert>
          ) : (
            <Table hover responsive>
              <thead>
                <tr>
                  <th>Modell</th>
                  <th>Version</th>
                  <th>Dateiname</th>
                  <th>Größe</th>
                  <th>Hochgeladen</th>
                  <th>Status</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {filteredFirmware.map(fw => (
                  <tr key={fw.id}>
                    <td>
                      <strong>{getModelName(fw.phone_model)}</strong>
                    </td>
                    <td>
                      <code>{fw.version}</code>
                    </td>
                    <td>
                      <span className="text-muted">{fw.original_filename}</span>
                    </td>
                    <td>{formatFileSize(fw.file_size)}</td>
                    <td>{formatDate(fw.created_at)}</td>
                    <td>
                      {fw.is_active ? (
                        <Badge bg="success">
                          <i className="bi bi-check-circle me-1"></i>
                          Aktiv
                        </Badge>
                      ) : (
                        <Badge bg="secondary">Inaktiv</Badge>
                      )}
                    </td>
                    <td>
                      <div className="btn-group btn-group-sm">
                        {!fw.is_active && (
                          <Button
                            variant="outline-success"
                            size="sm"
                            title="Als aktive Firmware setzen"
                            onClick={() => handleActivate(fw)}
                          >
                            <i className="bi bi-check-lg"></i>
                          </Button>
                        )}
                        <Button
                          variant="outline-primary"
                          size="sm"
                          title="Herunterladen"
                          as="a"
                          href={fw.download_url}
                          target="_blank"
                        >
                          <i className="bi bi-download"></i>
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          title="Löschen"
                          onClick={() => handleDelete(fw)}
                        >
                          <i className="bi bi-trash"></i>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Upload Modal */}
      <Modal show={showUploadModal} onHide={() => setShowUploadModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-upload me-2"></i>
            Firmware hochladen
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Telefonmodell *</Form.Label>
              <Form.Select
                value={uploadForm.phone_model}
                onChange={e => setUploadForm({ ...uploadForm, phone_model: e.target.value })}
              >
                <option value="">-- Modell auswählen --</option>
                {PHONE_MODELS.map(model => (
                  <option key={model.id} value={model.id}>{model.name}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Version *</Form.Label>
              <Form.Control
                type="text"
                placeholder="z.B. 96.86.0.15"
                value={uploadForm.version}
                onChange={e => setUploadForm({ ...uploadForm, version: e.target.value })}
              />
              <Form.Text className="text-muted">
                Die Firmware-Version (aus dem Dateinamen oder der Yealink-Website)
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Firmware-Datei *</Form.Label>
              <Form.Control
                type="file"
                accept=".rom,.bin,.fw"
                ref={fileInputRef}
              />
              <Form.Text className="text-muted">
                Unterstützte Formate: .rom, .bin, .fw
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Beschreibung</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Optional: Notizen zur Firmware"
                value={uploadForm.description}
                onChange={e => setUploadForm({ ...uploadForm, description: e.target.value })}
              />
            </Form.Group>

            <Form.Check
              type="checkbox"
              id="set_active"
              label="Als aktive Firmware für dieses Modell setzen"
              checked={uploadForm.set_active}
              onChange={e => setUploadForm({ ...uploadForm, set_active: e.target.checked })}
            />

            {uploading && (
              <ProgressBar
                animated
                now={uploadProgress || 100}
                label={uploadProgress > 0 ? `${uploadProgress}%` : 'Hochladen...'}
                className="mt-3"
              />
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUploadModal(false)} disabled={uploading}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={handleUpload} disabled={uploading}>
            {uploading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Hochladen...
              </>
            ) : (
              <>
                <i className="bi bi-upload me-2"></i>
                Hochladen
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
